import type { CaseStatus } from './cases.js';

/**
 * The account's story for the top of the customer page (Collections Phase 2, 2026-10-31): what the customer last said,
 * what we last did, and what happens next. Built only from timeline events and the case, so every line points at
 * something that happened. Pure: the page and the tests call it with the same inputs.
 */
export interface StoryEvent {
  at: string; kind: string; category: string; actor: 'agent' | 'person' | 'customer' | 'system';
  title: string; detail: string | null; notes?: string[]; amount?: { minor: string; currency: string } | null; href?: string;
  refs?: { messageId?: string };
}
export interface StoryCase {
  status: CaseStatus; situation: string; oldestDaysOverdue: number;
  next: { summary: string; status: string; date: string | null };
}
export interface StoryStep {
  kind: 'said' | 'did' | 'next'; label: string; text: string; note: string | null; at: string | null;
  who: 'customer' | 'agent' | 'person' | null; href: string | null;
}

/** What we did: our side of the conversation, never drafts still waiting (those are the next step) or ledger rows. */
const ours = new Set(['email_sent', 'email_shadow', 'call', 'documents', 'bounced']);
/** What they said: their words only (replies, promises, what they told a call). A payment is money, not a message. */
const theirs = (e: StoryEvent) => (e.actor === 'customer' && (e.kind === 'reply_received' || e.kind === 'promise'))
  || (e.kind === 'call' && (e.notes ?? []).some(n => n.startsWith('They said:')));
const clip = (text: string, max = 160) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text);

function said(events: StoryEvent[], c: StoryCase): StoryStep {
  const latest = events.find(theirs);
  const base = { kind: 'said' as const, label: 'What they said' };
  if (!latest) {
    return { ...base, text: c.oldestDaysOverdue > 0 ? 'Nothing yet' : 'Nothing yet, and nothing is overdue', note: null, at: null,
      who: null, href: null };
  }
  // A promise is the part of its reply that matters; they are paired by the message the promise came from.
  const source = latest.refs?.messageId;
  const promise = latest.kind === 'promise' ? latest
    : source ? events.find(e => e.kind === 'promise' && e.refs?.messageId === source) ?? null : null;
  if (promise) {
    return { ...base, text: clip(promise.title.replace(/^Promised/, 'They promised')), note: promise.detail ?? null, at: promise.at,
      who: 'customer', href: promise.href ?? null };
  }
  if (latest.kind === 'call') {
    const words = latest.notes!.find(n => n.startsWith('They said:'))!.replace(/^They said:\s*/, '');
    return { ...base, text: clip(`On a call: “${words}”`), note: latest.detail, at: latest.at, who: 'customer', href: latest.href ?? null };
  }
  return { ...base, text: clip(latest.notes?.[0] ?? latest.detail ?? latest.title), note: latest.notes?.[0] ? latest.detail : null,
    at: latest.at, who: 'customer', href: latest.href ?? null };
}

function did(events: StoryEvent[], persona: string): StoryStep {
  const latest = events.find(e => ours.has(e.kind) && (e.actor === 'agent' || e.actor === 'person' || e.kind === 'bounced'));
  const base = { kind: 'did' as const, label: 'What we did' };
  if (!latest) return { ...base, text: `${persona} has not contacted them yet`, note: null, at: null, who: null, href: null };
  return { ...base, text: clip(latest.title), note: latest.detail ? clip(latest.detail) : null, at: latest.at,
    who: latest.actor === 'person' ? 'person' : 'agent', href: latest.href ?? null };
}

function next(c: StoryCase, persona: string, events: StoryEvent[], plan: string | null): StoryStep {
  const base = { kind: 'next' as const, label: 'What happens next', at: c.next.date, who: null, href: null, note: null };
  const yours = c.next.status === 'you';
  switch (c.status) {
    // The policy step on the card below still stands; say how it relates to the decision instead of leaving two next steps.
    case 'needs_you': return yours
      ? { ...base, text: `Needs your decision: ${c.next.summary}`, at: null,
        note: plan && plan !== c.next.summary ? `After that, ${persona} plans: ${plan}` : null }
      : { ...base, text: 'Needs your decision: decide how to follow up', note: `Until then, ${persona} plans: ${plan ?? c.next.summary}` };
    case 'flagged': return { ...base, text: 'Needs your decision: decide how to follow up', note: `Until then, ${persona} plans: ${plan ?? c.next.summary}` };
    case 'settled': {
      const paid = events.find(e => e.kind === 'payment');
      return { ...base, text: 'Paid up. Nothing more to collect', at: paid?.at ?? null };
    }
    case 'paused': return { ...base, text: 'Reminders are paused', note: c.next.summary !== 'Reminders paused' ? c.next.summary : null };
    case 'waiting': return { ...base, text: `Waiting on them. Then ${persona}: ${c.next.summary}` };
    case 'other': return { ...base, text: 'Nothing planned', at: null };
    default: return { ...base, text: `${persona}: ${c.next.summary}` };
  }
}

/** `events` newest first, as the timeline returns them. `plan` is the policy's next step, shown on the card below. */
export function caseStory(c: StoryCase, events: StoryEvent[], persona: string, plan: string | null = null) {
  return { headline: c.situation, steps: [said(events, c), did(events, persona), next(c, persona, events, plan)] };
}
